import React, { createContext, useContext, useState, useEffect } from "react";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate checking for existing session
    const savedUser = localStorage.getItem("giss_user");
    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // Mock user data based on email
    let userData;
    if (email === "admin@giss.edu") {
      userData = {
        id: 1,
        name: "System Administrator",
        email: "admin@giss.edu",
        role: "admin",
        avatar: "👨‍💼",
        permissions: [
          "dashboard",
          "library_manage",
          "transportation_manage",
          "cafeteria_manage",
          "e_library_access",
          "admin",
        ],
      };
    } else if (email === "librarian@giss.edu") {
      userData = {
        id: 2,
        name: "Sarah Johnson",
        email: "librarian@giss.edu",
        role: "librarian",
        avatar: "👩‍💼",
        permissions: ["dashboard", "library_manage", "e_library_access"],
      };
    } else if (email === "teacher@giss.edu") {
      userData = {
        id: 3,
        name: "Michael Chen",
        email: "teacher@giss.edu",
        role: "teacher",
        avatar: "👨‍🏫",
        permissions: ["dashboard", "library_borrow", "e_library_access"],
      };
    } else if (email === "student@giss.edu") {
      userData = {
        id: 4,
        name: "Emma Wilson",
        email: "student@giss.edu",
        role: "student",
        avatar: "👩‍🎓",
        grade: "Grade 10",
        studentId: "STU001",
        permissions: [
          "dashboard",
          "library_request",
          "library_view_own",
          "cafeteria_purchase",
          "cafeteria_view_own",
          "e_library_access",
          "e_library_download",
        ],
      };
    } else if (email === "parent@giss.edu") {
      userData = {
        id: 5,
        name: "David Wilson",
        email: "parent@giss.edu",
        role: "parent",
        avatar: "👨‍👧‍👦",
        children: ["Emma Wilson"],
        permissions: [
          "dashboard",
          "library_view_child",
          "transportation_view_child",
          "cafeteria_controls",
          "cafeteria_view_child",
        ],
      };
    } else {
      throw new Error("Invalid credentials");
    }

    setUser(userData);
    localStorage.setItem("giss_user", JSON.stringify(userData));
    return userData;
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem("giss_user");
  };

  const hasPermission = (permission) => {
    if (!user) return false;
    if (user.permissions.includes("all")) return true;
    return user.permissions.includes(permission);
  };

  const value = {
    user,
    login,
    logout,
    hasPermission,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
